import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useApplicationStore } from '../store/application.store';
import { useNavigationStore } from '../store/navigation.store';
import { useSignerStore } from '../store/signer.store';
import { logger } from '@/features/help/utils/logger';
import { fileService } from '../services/file.service';
import { FieldValue } from '../types/field-value';
import { Signer } from '../types/signers';
import { signerApi } from '../api/signer.api';
import toast from 'react-hot-toast';
import { formAutosaveService } from '../services/form-autosave.service';
// Autosave moved to navigation layer after validation

interface UseFormContentProps {
  mappingShortId?: string;
}

interface UseFormContentReturn {
  // Terms consent
  isTermsInvalid: boolean;
  handleTermsConsentChange: (checked: boolean) => void;

  // Contact consent (first page)
  contactConsent: boolean;
  isContactConsentInvalid: boolean;
  handleContactConsentChange: (checked: boolean) => void;

  // Navigation handlers
  handleNextPageWithValidation: (
    isLastPage: boolean,
    isFirstPage: boolean,
    formRef: React.RefObject<HTMLDivElement>
  ) => Promise<void>;
  handlePreviousPageNavigation: () => void;

  // Form state
  formRef: React.RefObject<HTMLDivElement>;

  // Ownership + additional owner UI
  isOwnershipPage: boolean;
  isPrimarySigner: boolean;
  shouldShowOwnershipWarning: boolean;
  showSignersModal: boolean;
  openSignersModal: () => Promise<void>;
  closeSignersModal: () => void;

  // Additional signers list
  additionalSigners: Signer[];
  isLoadingSigners: boolean;
  hasFetchedAdditionalSigners: boolean;
  refetchSigners: () => Promise<void>;
  deleteSigner: (signerId: string) => Promise<void>;
  addOptimisticSigners: (emails: string[]) => void;
}

/**
 * Custom hook to handle FormContent logic including live signer sync,
 * file caching, terms consent, and navigation with validation
 */
export const useFormContent = ({ mappingShortId }: UseFormContentProps): UseFormContentReturn => {
  const {
    setTermsConsent,
    termsConsent,
    contactConsent,
    setContactConsent,
    handleFieldValueChange,
    structure,
    crmId,
    userShortId,
    pdfOnly,
    setCrmId,
  } = useApplicationStore();

  const { handleNextPage, handlePreviousPage, getCurrentPage } = useNavigationStore();
  const currentSignerIndex = useSignerStore((state) => state.currentSigner);

  const [isTermsInvalid, setIsTermsInvalid] = useState(false);
  const [isContactConsentInvalid, setIsContactConsentInvalid] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const currentPage = getCurrentPage();

  const [showSignersModal, setShowSignersModal] = useState(false);
  const [serverSigners, setServerSigners] = useState<Signer[]>([]);
  const [optimisticSigners, setOptimisticSigners] = useState<Signer[]>([]);
  const [isLoadingSigners, setIsLoadingSigners] = useState(false);
  const [hasFetchedAdditionalSigners, setHasFetchedAdditionalSigners] = useState(false);
  const [isReconcilingSigners, setIsReconcilingSigners] = useState(false);
  const reconcileTimeoutsRef = useRef<number[]>([]);
  const reconcileStopTimeoutRef = useRef<number | null>(null);
  const pendingOptimisticEmailsRef = useRef<string[]>([]);
  const isFetchingSignersRef = useRef(false);
  const lastFetchedCrmIdRef = useRef<string | null>(null);

  // Purpose: Normalize emails for comparisons (case-insensitive)
  const normalizeSignerEmail = useCallback((email: string) => email.trim().toLowerCase(), []);

  // Purpose: Combine server and optimistic signers without duplicates
  const additionalSigners = useMemo(() => {
    // Only treat non-deactivated server rows as "authoritative" for de-duping.
    // If a signer is deactivated, we still want optimistic pending_invite to render while the worker reactivates.
    const serverEmailSet = new Set(
      serverSigners
        .filter((s) => s.status !== 'deactivated')
        .map((s) => normalizeSignerEmail(s.email))
    );
    const filteredOptimistic = optimisticSigners.filter(
      (signer) => !serverEmailSet.has(normalizeSignerEmail(signer.email))
    );
    return [...serverSigners, ...filteredOptimistic];
  }, [serverSigners, optimisticSigners, normalizeSignerEmail]);

  // Purpose: Clear any scheduled signer reconciliation refetches (to avoid memory leaks)
  const clearSignerReconcileTimeouts = useCallback(() => {
    reconcileTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
    reconcileTimeoutsRef.current = [];
  }, []);

  // Purpose: Clear the "stop reconcile" timeout (used to avoid spinner blinking / hanging)
  const clearSignerReconcileStopTimeout = useCallback(() => {
    if (reconcileStopTimeoutRef.current != null) {
      window.clearTimeout(reconcileStopTimeoutRef.current);
      reconcileStopTimeoutRef.current = null;
    }
  }, []);

  // Purpose: Clear pending optimistic email tracking once the server has caught up
  const clearPendingOptimisticEmails = useCallback(() => {
    pendingOptimisticEmailsRef.current = [];
  }, []);

  // Purpose: Ensure timeouts are cleared if the hook unmounts
  useEffect(() => {
    return () => {
      clearSignerReconcileTimeouts();
      clearSignerReconcileStopTimeout();
    };
  }, [clearSignerReconcileTimeouts, clearSignerReconcileStopTimeout]);

  // Purpose: Optimistically add additional owners to the list immediately after submit
  const addOptimisticSigners = useCallback(
    (emails: string[]) => {
      const normalizedInput = emails
        .map((email) => email.trim())
        .filter((email) => email.length > 0);

      if (normalizedInput.length === 0) {
        return;
      }

      // Purpose: Track which emails we're waiting for the server to confirm as active
      pendingOptimisticEmailsRef.current = normalizedInput.map(normalizeSignerEmail);
      setIsReconcilingSigners(true);
      clearSignerReconcileStopTimeout();

      setOptimisticSigners((prev) => {
        const existing = [...serverSigners, ...prev];
        const existingEmailSet = new Set(existing.map((s) => normalizeSignerEmail(s.email)));
        let maxIndex = existing.reduce((max, signer) => Math.max(max, signer.signer_index), 1);

        const now = Date.now();
        const newSigners: Signer[] = [];

        normalizedInput.forEach((email, index) => {
          const normalizedEmail = normalizeSignerEmail(email);
          if (existingEmailSet.has(normalizedEmail)) {
            return;
          }

          maxIndex += 1;
          existingEmailSet.add(normalizedEmail);

          newSigners.push({
            signer_id: `optimistic-${now}-${index}`,
            email,
            signer_index: maxIndex,
            status: 'pending_invite',
            role: 'Signer',
          });
        });

        if (newSigners.length === 0) {
          return prev;
        }

        logger.info('FormContent Hook', 'Added optimistic additional signers', {
          addedCount: newSigners.length,
          emails: newSigners.map((signer) => signer.email),
        });

        return [...prev, ...newSigners];
      });

      // Purpose: Background-refetch a few times to reconcile optimistic entries once the worker has updated the DB
      // (especially important when re-inviting a previously deactivated signer, where the first fetch may still show "deactivated")
      clearSignerReconcileTimeouts();
      const retryScheduleMs = [600, 1500, 3000];
      retryScheduleMs.forEach((delayMs, attemptIndex) => {
        const timeoutId = window.setTimeout(async () => {
          try {
            logger.debug('FormContent Hook', 'Reconcile fetch for optimistic signers', {
              attemptIndex,
              delayMs,
              emailCount: normalizedInput.length,
            });
            await fetchAdditionalSigners();
          } catch (error) {
            logger.warn('FormContent Hook', 'Reconcile fetch failed (non-blocking)', {
              attemptIndex,
              delayMs,
              error,
            });
          }
        }, delayMs);
        reconcileTimeoutsRef.current.push(timeoutId);
      });

      // Purpose: Ensure we don't keep a reconciliation spinner forever if the worker never updates the DB.
      // Keep it long enough to cover the retry window.
      reconcileStopTimeoutRef.current = window.setTimeout(() => {
        logger.warn('FormContent Hook', 'Stopping signer reconciliation after timeout (non-blocking)', {
          pendingEmailCount: pendingOptimisticEmailsRef.current.length,
        });
        clearSignerReconcileTimeouts();
        clearPendingOptimisticEmails();
        setIsReconcilingSigners(false);
      }, 7000);
    },
    [
      serverSigners,
      normalizeSignerEmail,
      clearSignerReconcileTimeouts,
      clearSignerReconcileStopTimeout,
      clearPendingOptimisticEmails,
    ]
  );

  /**
   * Fetches the list of signers for the current record from the backend.
   * Only fetches if we have the required identifiers (userShortId, mappingShortId, crmId).
   */
  const fetchAdditionalSigners = useCallback(async () => {
    if (isFetchingSignersRef.current) {
      logger.debug('FormContent Hook', 'Skipping signers fetch - already in progress');
      return;
    }

    if (!userShortId || !mappingShortId || !crmId) {
      logger.debug('FormContent Hook', 'Skipping signers fetch - missing identifiers', {
        hasUserShortId: !!userShortId,
        hasMappingShortId: !!mappingShortId,
        hasCrmId: !!crmId,
      });
      return;
    }

    isFetchingSignersRef.current = true;
    setIsLoadingSigners(true);
    try {
      const signers = await signerApi.getSigners(userShortId, mappingShortId, crmId);
      setServerSigners(signers);
      setOptimisticSigners((prev) => {
        // Only clear optimistic entries when the server has a non-deactivated signer for that email.
        // If the server row exists but is still deactivated, keep the optimistic pending_invite visible.
        const serverActiveEmailSet = new Set(
          signers
            .filter((s) => s.status !== 'deactivated')
            .map((s) => normalizeSignerEmail(s.email))
        );
        const filtered = prev.filter(
          (signer) => !serverActiveEmailSet.has(normalizeSignerEmail(signer.email))
        );

        if (filtered.length !== prev.length) {
          logger.info('FormContent Hook', 'Reconciled optimistic signers after fetch', {
            removedCount: prev.length - filtered.length,
          });
        }

        return filtered;
      });

      // Purpose: Stop reconciliation once the server confirms all pending emails as active (not deactivated)
      const pendingEmails = pendingOptimisticEmailsRef.current;
      if (pendingEmails.length > 0) {
        const activeEmailSet = new Set(
          signers
            .filter((s) => s.status !== 'deactivated')
            .map((s) => normalizeSignerEmail(s.email))
        );

        const missing = pendingEmails.filter((email) => !activeEmailSet.has(email));
        if (missing.length === 0) {
          logger.info('FormContent Hook', 'Server confirmed all optimistic signers as active', {
            emailCount: pendingEmails.length,
          });
          clearSignerReconcileTimeouts();
          clearSignerReconcileStopTimeout();
          clearPendingOptimisticEmails();
          setIsReconcilingSigners(false);
        }
      }

      logger.info('FormContent Hook', 'Fetched additional signers', {
        count: signers.length,
        additionalCount: signers.filter((s) => s.signer_index > 1).length,
      });
    } catch (error) {
      logger.error('FormContent Hook', 'Failed to fetch additional signers', { error });
      setServerSigners([]);
    } finally {
      setIsLoadingSigners(false);
      isFetchingSignersRef.current = false;
      setHasFetchedAdditionalSigners(true);
    }
  }, [
    userShortId,
    mappingShortId,
    crmId,
    normalizeSignerEmail,
    clearSignerReconcileTimeouts,
    clearSignerReconcileStopTimeout,
    clearPendingOptimisticEmails,
  ]);

  useEffect(() => {
    if (!crmId) {
      setHasFetchedAdditionalSigners(false);
      lastFetchedCrmIdRef.current = null;
      return;
    }

    if (lastFetchedCrmIdRef.current && lastFetchedCrmIdRef.current !== crmId) {
      setHasFetchedAdditionalSigners(false);
      lastFetchedCrmIdRef.current = null;
    }
  }, [crmId]);

  // Fetch additional signers as soon as identifiers are available (primary signer only)
  useEffect(() => {
    if (currentSignerIndex !== 1) {
      return;
    }

    if (!userShortId || !mappingShortId || !crmId) {
      return;
    }

    if (lastFetchedCrmIdRef.current === crmId) {
      return;
    }

    lastFetchedCrmIdRef.current = crmId;
    fetchAdditionalSigners();
  }, [currentSignerIndex, userShortId, mappingShortId, crmId, fetchAdditionalSigners]);

  // Load cached files for file fields on mount
  useEffect(() => {
    if (!currentPage?.sections) return;

    currentPage.sections.forEach((section: any) => {
      section.fields.forEach((field: any) => {
        if (field.type === 'file') {
          // Check if we have files in the cache AND the field doesn't already have a value
          if (
            fileService.hasFiles(field.id) &&
            (!field.value || (Array.isArray(field.value) && field.value.length === 0))
          ) {
            const cachedFiles = fileService.getFiles(field.id);

            // Update the field value with cached files
            if (cachedFiles.length > 0) {
              handleFieldValueChange(field.id, cachedFiles as FieldValue);
            }
          }
        }
      });
    });
  }, [currentPage, handleFieldValueChange]);

  // Handle terms consent change
  const handleTermsConsentChange = (checked: boolean) => {
    setTermsConsent(checked);

    if (checked) {
      setIsTermsInvalid(false);
    }
  };

  // Handle first-page contact consent change
  const handleContactConsentChange = (checked: boolean) => {
    setContactConsent(checked);

    if (checked) {
      setIsContactConsentInvalid(false);
    }
  };

  // Handle next page with validation for terms consent
  const handleNextPageWithValidation = async (
    isLastPage: boolean,
    isFirstPage: boolean,
    formRefElement: React.RefObject<HTMLDivElement>
  ) => {

    if (isFirstPage && !contactConsent) {
      setIsContactConsentInvalid(true);
      toast.error('You must agree to contact consent to continue');
      logger.info('FormContent Hook', 'Contact consent required but not given', {
        isFirstPage,
        contactConsent,
      });
      return;
    }

    if (isLastPage && !termsConsent) {
      setIsTermsInvalid(true);
      toast.error('You must agree to the terms to continue');
      logger.info('FormContent Hook', 'Terms consent required but not given', {
        isLastPage,
        termsConsent,
      });
      return;
    }

    if (shouldBlockForOwnershipRequirement) {
      toast.error('Minimum 51% ownership requires adding an owner before continuing.');
      logger.info('FormContent Hook', 'Blocked navigation due to ownership under 51%', {
        owner1OwnershipPercentage,
        activeAdditionalOwnersCount,
      });
      await openSignersModal();
      return;
    }

    // Set page completion timestamp (for pages 1-3) before navigating
    try {
      const { activePage } = useNavigationStore.getState();
      const timestampFieldIds: string[] = [
        'page1CompleteTimestamp',
        'page2CompleteTimestamp',
        'page3CompleteTimestamp',
      ];
      const timestampFieldId = timestampFieldIds[activePage];

      if (timestampFieldId) {
        const timestamp = new Date().toISOString();
        handleFieldValueChange(timestampFieldId, timestamp as FieldValue);
        logger.info('FormContent Hook', 'Set page completion timestamp before navigation', {
          pageIndex: activePage,
          fieldId: timestampFieldId,
          timestamp,
          mappingShortId,
        });
      }
    } catch (error) {
      logger.warn('FormContent Hook', 'Failed to set page completion timestamp (non-blocking)', {
        error,
        mappingShortId,
      });
    }

    // Delegate to navigation which validates first and autosaves after validation
    await handleNextPage(formRefElement.current, mappingShortId);
  };

  const handlePreviousPageNavigation = () => {
    handlePreviousPage();
  };

  // Determine page context
  const isOwnershipPage =
    !!currentPage?.sections?.some((section) =>
      section.fields.some((field) => field.id === 'owner1OwnershipPercentage')
    );
  const isPrimarySigner = currentSignerIndex === 1;

  // Derive Owner 1 ownership percentage from the form structure
  let owner1OwnershipPercentage: number | null = null;
  if (structure?.pages) {
    for (const page of structure.pages) {
      for (const section of page.sections) {
        for (const field of section.fields) {
          if (field.id === 'owner1OwnershipPercentage') {
            const raw = field.value;
            if (typeof raw === 'number') {
              owner1OwnershipPercentage = raw;
            } else if (typeof raw === 'string' && raw.trim() !== '') {
              const numeric = parseFloat(raw.replace('%', '').trim());
              if (!Number.isNaN(numeric)) {
                owner1OwnershipPercentage = numeric;
              }
            }
          }
        }
      }
    }
  }

  const shouldShowOwnershipWarning =
    isOwnershipPage &&
    isPrimarySigner &&
    owner1OwnershipPercentage !== null &&
    owner1OwnershipPercentage < 51;

  const activeAdditionalOwnersCount = additionalSigners.filter(
    (signer) => signer.signer_index > 1 && signer.status !== 'deactivated'
  ).length;

  const shouldBlockForOwnershipRequirement =
    isOwnershipPage &&
    isPrimarySigner &&
    !pdfOnly &&
    owner1OwnershipPercentage !== null &&
    owner1OwnershipPercentage < 51 &&
    activeAdditionalOwnersCount === 0;

  const ensureCrmIdForSignerActions = useCallback(async (): Promise<string | null> => {
    if (crmId) {
      return crmId;
    }

    if (!mappingShortId) {
      toast.error('Unable to add an owner right now. Please refresh and try again.');
      logger.warn('FormContent Hook', 'Blocked owner modal open: missing mappingShortId');
      return null;
    }

    const autosaveResult = await formAutosaveService.autosave(mappingShortId);
    if (!autosaveResult.success || !autosaveResult.crmId) {
      toast.error(
        'We need to save your application before inviting an owner. Please try again in a moment.'
      );
      logger.warn('FormContent Hook', 'Failed to ensure crmId before owner modal open', {
        mappingShortId,
        autosaveSuccess: autosaveResult.success,
        hasCrmId: !!autosaveResult.crmId,
      });
      return null;
    }

    const resolvedCrmId = autosaveResult.crmId;
    setCrmId(resolvedCrmId);

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('crmId', String(resolvedCrmId));
      url.searchParams.set('signerIndex', String(currentSignerIndex || 1));
      window.history.replaceState({}, '', url.toString());
    } catch (error) {
      logger.warn('FormContent Hook', 'Failed to update URL after ensuring crmId', {
        error,
        resolvedCrmId,
      });
    }

    return resolvedCrmId;
  }, [crmId, mappingShortId, setCrmId, currentSignerIndex]);

  const openSignersModal = async () => {
    if (!userShortId || !mappingShortId) {
      toast.error('Unable to add an owner right now. Please refresh and try again.');
      logger.warn('FormContent Hook', 'Blocked opening SignersModal due to missing identifiers', {
        mappingShortId,
        hasUserShortId: !!userShortId,
      });
      return;
    }

    const resolvedCrmId = await ensureCrmIdForSignerActions();
    if (!resolvedCrmId) {
      return;
    }

    setShowSignersModal(true);
  };

  const closeSignersModal = async () => {
    setShowSignersModal(false);
    await fetchAdditionalSigners();
  };

  /**
   * Deletes a signer by their ID and refetches the signers list.
   */
  const deleteSigner = useCallback(
    async (signerId: string) => {
      if (signerId.startsWith('optimistic-')) {
        setOptimisticSigners((prev) => prev.filter((signer) => signer.signer_id !== signerId));
        logger.info('FormContent Hook', 'Removed optimistic signer before server confirmation', {
          signerId,
        });
        toast.success('Signer removed successfully');
        return;
      }

      if (!userShortId || !mappingShortId || !crmId) {
        toast.error('Unable to delete signer. Please try again.');
        return;
      }

      try {
        await signerApi.deleteSigner(userShortId, mappingShortId, crmId, signerId);
        logger.info('FormContent Hook', 'Deleted signer', { signerId });
        toast.success('Signer removed successfully');
        await fetchAdditionalSigners();
      } catch (error) {
        logger.error('FormContent Hook', 'Failed to delete signer', { signerId, error });
        toast.error('Failed to remove signer. Please try again.');
      }
    },
    [userShortId, mappingShortId, crmId, fetchAdditionalSigners]
  );

  return {
    // Terms consent
    isTermsInvalid,
    handleTermsConsentChange,

    // Contact consent
    contactConsent,
    isContactConsentInvalid,
    handleContactConsentChange,

    // Navigation handlers
    handleNextPageWithValidation,
    handlePreviousPageNavigation,

    // Form state
    formRef,

    // Ownership + additional owner UI
    isOwnershipPage,
    isPrimarySigner,
    shouldShowOwnershipWarning,
    showSignersModal,
    openSignersModal,
    closeSignersModal,

    // Additional signers list
    additionalSigners,
    isLoadingSigners: isLoadingSigners || isReconcilingSigners,
    hasFetchedAdditionalSigners,
    refetchSigners: fetchAdditionalSigners,
    deleteSigner,
    addOptimisticSigners,
  };
};
